import bluebird from 'bluebird'
import CreateReducer from '../utils/createReducer'
import CreateActions from '../utils/createActions'

import {
  handlers,
} from '../utils/api'

import networkWrapper from '../utils/networkWrapper'
import messagebusActions from './messagebus'

const prefix = 'launch'
const wrapper = networkWrapper.factory(prefix)

const initialState = {
  initialised: false,
  config: null,
  pool: null,
  lease: null,
  vm: null,
  stage: 'pool',
  poolLogs: [],
  vmLogs: [],
}

const reducers = {
  setInitialised: (state, action) => {
    state.initialised = action.payload
  },
  setConfig: (state, action) => {
    state.config = action.payload
  },
  setPool: (state, action) => {
    state.pool = action.payload
  },
  setLease: (state, action) => {
    state.lease = action.payload
  },
  setVm: (state, action) => {
    state.vm = action.payload
  },
  setStage: (state, action) => {
    state.stage = action.payload
  },
  addPoolLogs: (state, action) => {
    state.poolLogs.push(action.payload)
  },
  addVmLogs: (state, action) => {
    state.vmLogs.push(action.payload)
  },
}

const isPoolReady = (pool) => pool.state == 'ready'
const isLeaseReady = (lease) => lease.state == 'ready'
const isLeaseAssigned = (lease) => lease.state == 'assigned' || isLeaseReady(lease)

const sideEffects = {

  create: ({
    repo,
    file,
  }) => wrapper('create', async (dispatch, getState) => {
    const {
      config,
      pool,
    } = await handlers.post(`/launch`, {
      repo,
      file,
    })
    dispatch(actions.setConfig(config))
    dispatch(actions.setPool(pool))
    dispatch(actions.setInitialised(true))

    if(pool.state == 'error') throw new Error(`pool error: ${pool.status}`)

    if(!isPoolReady(pool)) {
      await dispatch(actions.waitForPool({pool}))
    }

    dispatch(actions.setStage('lease'))

    let lease = await handlers.post(`/pools/${pool.id}/leases`, {
      pool: pool.id,
      state: 'waiting',
      meta: {},
    })

    if(!isLeaseAssigned(lease)) {
      await dispatch(actions.waitForLeaseAssigned({lease}))
    }

    dispatch(actions.setStage('vm'))

    lease = await handlers.get(`/pools/${lease.pool}/leases/${lease.id}`)

    if(!isLeaseReady(lease)) {
      await dispatch(actions.waitForVm({lease}))
    }

    lease = await handlers.get(`/pools/${lease.pool}/leases/${lease.id}`)
    const vm = await handlers.get(`/pools/${lease.pool}/vms/${lease.vm}`)
    dispatch(actions.setLease(lease))
    dispatch(actions.setVm(vm))
    dispatch(actions.setStage('ready'))

  }, {
    globalLoading: false,
  }),

  waitForPool: ({
    pool,
  }) => async (dispatch, getState) => {

    let ready = false

    await dispatch(messagebusActions.subscribeToPool(pool, ({
      channel,
      messageType,
      body,
    }) => {
      if(messageType != 'pool_update') return
      const updatedPool = JSON.parse(body)
      if(isPoolReady(updatedPool)) ready = true
    }))

    await dispatch(messagebusActions.subscribeToPoolLogs(pool, ({
      channel,
      messageType,
      body,
    }) => {
      const logs = JSON.parse(body)
      dispatch(actions.addPoolLogs(logs.text))
    }))

    while(!ready) {
      await bluebird.delay(1000)
      const updatedPool = await handlers.get(`/pools/${pool.id}`)
      if(isPoolReady(updatedPool)) ready = true
    }

    await dispatch(messagebusActions.unsubscribeToPool(pool))
    await dispatch(messagebusActions.unsubscribeToPoolLogs(pool))
  },

  waitForLease: ({
    lease,
    checkFunction,
  }) => async (dispatch, getState) => {

    let ready = false

    await dispatch(messagebusActions.subscribeToLease(lease, ({
      channel,
      messageType,
      body,
    }) => {
      if(messageType != 'lease_update') return
      const updatedLease = JSON.parse(body)
      if(checkFunction(updatedLease)) ready = true
    }))

    while(!ready) {
      await bluebird.delay(1000)
      const newLease = await handlers.get(`/pools/${lease.pool}/leases/${lease.id}`)
      if(checkFunction(newLease)) ready = true
    }

    await dispatch(messagebusActions.unsubscribeToLease(lease))
  },

  waitForLeaseAssigned: ({
    lease,
  }) => async (dispatch, getState) => {
    await dispatch(actions.waitForLease({
      lease,
      checkFunction: isLeaseAssigned,
    }))
  },

  waitForLeaseReady: ({
    lease,
  }) => async (dispatch, getState) => {
    await dispatch(actions.waitForLease({
      lease,
      checkFunction: isLeaseReady,
    }))
  },

  waitForVm: ({
    lease,
  }) => async (dispatch, getState) => {

    await dispatch(messagebusActions.subscribeToVmLogs(lease.pool, lease.vm, ({
      channel,
      messageType,
      body,
    }) => {
      const logs = JSON.parse(body)
      dispatch(actions.addVmLogs(logs.text))
    }))

    await dispatch(actions.waitForLeaseReady({
      lease,
    }))

    await dispatch(messagebusActions.subscribeToVmLogs(lease.pool, lease.vm))
  }
}

const reducer = CreateReducer({
  initialState,
  reducers,
  prefix,
})

const actions = CreateActions({
  reducers,
  sideEffects,
  prefix,
})

export { actions, reducer }
export default actions
